import { api, type SectionDraftDetail } from '../../api/client'
import type { Box, FitKey } from './framing'

const SD = '/api/lab/section-drafts'

/** Every coordinate in these types is a fraction of the ROOT original photograph, whichever asset
 *  (the original or a trim of it) the piece shows. (PieceDetail.featured_framing is different: it is
 *  in fractions of the photo SHOWN.) */
export type Framing = {
  asset_id: number
  original: { asset_id: number; src: string; width: number | null; height: number | null }
  /** The trim the asset in use was cut with, or null when it is the original. */
  crop_box: number[] | null
  suggestion: { subject_box: number[] | null; focal_x: number | null; focal_y: number | null } | null
  /** The piece's own framing (featured photo only). */
  piece: {
    focal_x: number | null
    focal_y: number | null
    subject_box: number[] | null
    fit: Record<string, string> | null
  } | null
  /** `fits`: the slot's best cover crop keeps at least KEEP_SHARE of the box; `kept` is that share. */
  slots: { key: string; ratio: number; fits: boolean; kept: number }[]
}

/** Body of the featured-framing save; coordinates are fractions of the root original. */
export type FeaturedFramingBody = {
  focal_x?: number | null
  focal_y?: number | null
  subject_box?: Box | number[] | null
  fit?: Partial<Record<FitKey, 'cover' | 'contain'>> | null
  crop_box?: Box | number[] | null
  restore_original?: boolean
}

export type CropOut = {
  asset_id: number
  src: string
  width: number | null
  height: number | null
  caption: string | null
  alt: string | null
}

/** A photograph's framing: the featured photo's, or (assetId) a body figure's. */
export const getFraming = (id: number, assetId?: number) =>
  api.get<Framing>(`${SD}/${id}/framing${assetId != null ? `?asset_id=${assetId}` : ''}`)

/** Ask the model where the subject is (once per original). */
export const suggestFraming = (id: number, assetId: number) =>
  api.post<Framing>(`${SD}/${id}/framing/suggest`, { asset_id: assetId })

/** Frame or trim the featured photo, or restore its original. */
export const saveFeaturedFraming = (id: number, body: FeaturedFramingBody) =>
  api.put<SectionDraftDetail>(`${SD}/${id}/framing/featured`, body)

/** Trim a body figure; returns the crop. */
export const cropFigure = (id: number, assetId: number, box: Box) =>
  api.post<CropOut>(`${SD}/${id}/crops`, { asset_id: assetId, box })
